import { loadingActions } from '@/actions';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import { Button } from '@/components/elements/forms/buttons';
import { getPlaceTimezone } from '@/helpers';
import { useAppDispatch } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _s, getLang } from '@/locale';
import { bookServices } from '@/services';
import { Place, TimeSlot } from '@/types/state/shared';
import moment from 'moment';
import { useReducer } from 'react';
import ListViewItem from './ListViewItem';

type TimeSlotsPerDay = {
  [key: number]: {
    day: number;
    program: TimeSlot[];
  };
};

const getTimeSlotsPerDay = (timeSlots: TimeSlot[]) => {
  const slots: TimeSlotsPerDay = {};
  timeSlots.forEach((timeSlot, i) => {
    const timeSlotMoment = moment(timeSlot.start);
    const startOfDayUnix = timeSlotMoment.clone().utc(true).startOf('day').unix();
    const hour = timeSlotMoment.clone().utc(true).unix() - startOfDayUnix;
    timeSlot.from = hour;

    if (!slots[startOfDayUnix]) {
      slots[startOfDayUnix] = {
        day: startOfDayUnix * 1000,
        program: [],
      };
    }

    slots[startOfDayUnix].program.push(timeSlot);
  });

  return slots;
};

const getListViewSchedule = async (placeId: number, start: string) => {
  const { start: newStart, timeSlots, loadMore } = await bookServices.getListViewSchedule(placeId, start);

  const slots = timeSlots.map((timeSlot) => {
    const timeSlotMoment = moment(timeSlot.start);
    const startOfDayUnix = timeSlotMoment.clone().utc(true).startOf('day').unix();
    const hour = timeSlotMoment.clone().utc(true).unix() - startOfDayUnix;
    timeSlot.from = hour;
    return timeSlot;
  });

  return {
    start: newStart,
    timeSlots: slots,
    loadMore,
  };
};

export type ListViewReducerState = {
  loadMore: boolean;
  start?: string;
  timeSlots: TimeSlot[];
  timeSlotsPerDay?: TimeSlotsPerDay;
  loading?: boolean;
  searchUntil?: boolean;
};

export type ListViewReducerAction =
  | {
      type: 'LOAD_MORE';
      payload: {
        loadMore?: boolean;
        start?: string;
        timeSlots?: TimeSlot[];
        timeSlotsPerDay?: TimeSlotsPerDay;
        setSearchUntil?: boolean;
      };
    }
  | {
      type: 'SET_LOADING';
      payload: {
        loading: boolean;
      };
    };

const ListViewReducer = (state: ListViewReducerState, action: ListViewReducerAction) => {
  switch (action.type) {
    case 'LOAD_MORE':
      return {
        ...state,
        loadMore: action.payload.loadMore,
        start: action.payload.start,
        timeSlots: action.payload.timeSlots,
        timeSlotsPerDay: action.payload.timeSlotsPerDay,
        loading: false,
        searchUntil: action.payload.setSearchUntil,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload.loading,
      };
    default:
      return state;
  }
};

const ListView = ({ place }: { place: Place }) => {
  const { isMobileView } = useMobileView();
  moment.tz.setDefault(getPlaceTimezone(place));
  const { schedule = {} } = place;
  const appDispatch = useAppDispatch();
  const initialState: ListViewReducerState = {
    loadMore: schedule.loadMore || false,
    start: schedule.start || undefined,
    timeSlots: schedule.timeSlots || [],
    timeSlotsPerDay: getTimeSlotsPerDay(schedule.timeSlots || []),
  };
  const [state, dispatch] = useReducer(ListViewReducer, initialState);
  const startDate = moment(state.start);
  startDate.locale(getLang());

  const handleLoadMore = async (e) => {
    if (!state.loading) {
      appDispatch(loadingActions.show());
      dispatch({ type: 'SET_LOADING', payload: { loading: true } });
      const { loadMore, start, timeSlots: newTimeSlots } = await getListViewSchedule(place.id, state.start);
      const slots = state.timeSlots.concat(newTimeSlots);
      appDispatch(loadingActions.hide());
      dispatch({
        type: 'LOAD_MORE',
        payload: {
          loadMore,
          start,
          timeSlots: slots,
          timeSlotsPerDay: getTimeSlotsPerDay(slots),
          setSearchUntil: Boolean(start && slots.length === 0),
        },
      });
    }
  };

  const services = Object.keys(state.timeSlotsPerDay).map((key, i) => (
    <ListViewItem
      place={place}
      schedule={state.timeSlotsPerDay[key]}
      key={i}
      index={i}
      isOpen={i < 30 || i === 0 ? true : false}
    />
  ));

  const noServices = (
    <>
      <p className="no-items mb-md">{_s('noServices')}</p>
      <img src="/images/no-service-ph.svg" alt="" className="mx-auto" />
    </>
  );

  return (
    <div id="services" className="services list-view">
      {services && services.length > 0 ? services : noServices}
      <div className={`${isMobileView ? 'h-sm' : 'h-xl'} relative ml-[-4px]`}>
        {state.loading && <LoadingPlaceHolder className="z-8 h-[92px] bg-top" />}
      </div>
      <div className={`${isMobileView ? 'ml-xl' : ''}`}>
        {state.loadMore && (
          <Button className="mr-xs" onClick={handleLoadMore}>
            {_s('extendButton')}
          </Button>
        )}
        {state.searchUntil && (
          <span className="mt-sm block">{_s('searchedUntil', { time: startDate.format('MMM YYYY') })}</span>
        )}
      </div>
    </div>
  );
};

export default ListView;
